import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App';
import { AppUpdateBanner } from './components/AppUpdateBanner';
import { LeagueDataUpdateBanner } from './components/LeagueDataUpdateBanner';
import { QueryProvider } from './context/QueryProvider';
import { AuthProvider } from './context/AuthProvider';
import {
  reloadForStaleChunk,
  isStaleChunkError,
  isOffline,
  getChunkReloadDiagnostics,
} from './utils/chunkReload';
import { isNoiseEvent } from './utils/sentryNoise';
import { AppErrorFallback } from './components/AppErrorFallback';
import { initSwClientContext } from './utils/swClientContext';
import { initLaunchHandler } from './utils/launchHandler';
import { consumeUpdatedMarker } from './utils/staleAppGuard';
import './index.css';

// v6.11.208 — Paneles del staff de playoffs (juez de cancha, mesa de control,
// verificación): ahí no se graban sesiones ni se miden visitas. Son pantallas que
// se re-renderizan cada segundo (relojes, cuentas regresivas) en celulares de
// gama media con WiFi de cancha, y la grabación (Clarity, replay de Sentry)
// observa cada cambio del DOM. Los errores sí se siguen reportando, con su
// replay: es justo lo que hace falta para depurar un problema de la jornada.
const isStaffRoute = /^\/(juez|abridor|recepcion|verificacion)(\/|$)/.test(window.location.pathname);

// v6.11.235 — /render/story/ la abre el worker de historias (Chromium sin
// cabeza) para fotografiar una placa: sin service worker (nada de banners de
// "nueva versión" ni caches), sin analytics ni grabación de sesión.
const isRenderRoute = window.location.pathname.startsWith('/render/');

// Initialize Sentry for error monitoring
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, // Capture 100% of transactions in production, adjust as needed
  // Session Replay
  replaysSessionSampleRate: isStaffRoute || isRenderRoute ? 0 : 0.1, // 10% of sessions (0 en paneles de staff)
  replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
  // Environment
  environment: import.meta.env.MODE,
  // Release tracking
  release: import.meta.env.VITE_APP_VERSION,
  // Only enable in production
  enabled: import.meta.env.PROD,

  // Filter out expected business errors and external noise
  beforeSend(event, hint) {
    const error = hint.originalException;
    const errorMessage = typeof error === 'string' ? error : (error as { message?: string } | null | undefined)?.message || '';
    const errorStack = event.exception?.values?.[0]?.stacktrace?.frames || [];

    // Filter Instagram/WebView Java bridge errors (external, not controllable)
    if (
      errorMessage.includes('Java object is gone') ||
      errorMessage.includes('enableButtonsClickedMetaDataLogging') ||
      errorMessage.includes('JavascriptInterface')
    ) {
      console.warn('[Sentry] Filtered Instagram WebView error:', errorMessage);
      return null; // Don't send to Sentry
    }

    // Filter expected business flow errors (not bugs)
    const expectedErrors = [
      'USER_NOT_FOUND',
      'USER_BLOCKED',
      'USER_REPLACED',
      'TEAM_DELETED'
    ];

    if (expectedErrors.includes(errorMessage)) {
      console.info('[Sentry] Filtered expected business error:', errorMessage);
      return null; // Don't send to Sentry
    }

    // v6.11.149 — Ruido que NO es un bug de la app: un fallo de red del navegador
    // en la redacción de WebKit ('Load failed' — el equivalente iOS de los
    // 'Failed to fetch' / 'NetworkError' que `ignoreErrors` ya descarta) o un
    // stack sin un solo frame nuestro (el script que inyecta el navegador in-app
    // de Instagram bajo el esquema `iabjs://`). El detalle, y por qué el filtro
    // anterior no los reconocía, en utils/sentryNoise.ts.
    //
    // El mensaje puede no venir en `hint.originalException` (un rechazo cuya razón
    // no es un Error llega con `originalException` inútil pero el evento ya trae
    // el texto ya normalizado). Se miran los dos, sin tocar `errorMessage`, que
    // lo consumen los filtros de arriba.
    const noiseMessage = errorMessage || event.exception?.values?.[0]?.value || '';
    if (isNoiseEvent(noiseMessage, errorStack)) {
      console.warn('[Sentry] Filtered browser/third-party noise:', noiseMessage);
      return null;
    }

    // v6.11.67 — Un chunk que no carga SIN conexión no es un bug: es el celular
    // sin señal. Reportarlo sólo ensucia (LIGA-AJUPAM-51/52/53/54 mezclaban los
    // dos casos y no había forma de separarlos desde Sentry).
    if (isStaleChunkError(error) && isOffline()) {
      console.warn('[Sentry] Filtered chunk error while offline:', errorMessage);
      return null;
    }

    // v6.11.67 — Si el error llegó hasta acá tras un fallo de chunk, adjuntar en
    // qué etapa se rindió la recuperación. Sin esto el evento no dice si el
    // deploy dejó los assets rotos o si la conexión se cayó, que es exactamente
    // lo que no se pudo determinar al triagear LIGA-AJUPAM-51/52/53/54.
    const chunkDiagnostics = getChunkReloadDiagnostics();
    if (chunkDiagnostics && isStaleChunkError(error)) {
      event.contexts = { ...event.contexts, chunkReload: { ...chunkDiagnostics } };
      event.tags = { ...event.tags, chunk_give_up_stage: String(chunkDiagnostics.stage) };
    }

    return event; // Send all other errors
  },

  // Ignore specific error patterns
  ignoreErrors: [
    // Browser/WebView errors
    'Java object is gone',
    'JavascriptInterface',
    'Non-Error promise rejection captured',
    // Navegador in-app de Instagram en iOS: inyecta su propio script de tracking
    // (sendDataToNative / sendPageHideMessage) en la página y llama a
    // window.webkit.messageHandlers sin guardarlo. No es código nuestro — no está
    // en index.html ni en src/ — y no hay nada que arreglar del lado de la app.
    'window.webkit.messageHandlers',
    // v6.11.353 — Lo mismo, del navegador in-app de la app de Google en iOS
    // (LIGA-AJUPAM-5A, browser "Google 442.x"): su script le manda un mensaje a
    // la app por el puente de WebKit y, si la persona cierra la pestaña antes de
    // que llegue, iOS rechaza esa promesa con este texto. Sin stacktrace y sin
    // código nuestro detrás. Es un texto fijo de WebKit: no tapa nada propio.
    'The WKWebView was deallocated before the message was delivered',

    // Expected network errors
    'NetworkError',
    'Failed to fetch',

    // Browser extension errors
    /extensions\//i,
    /^chrome:\/\//i,
    /^moz-extension:\/\//i,
  ],
});

// Initialize Microsoft Clarity for UX analytics (production only, excluding admin routes)
const isAdminRoute = window.location.pathname.includes('/admin') || isStaffRoute || isRenderRoute;
if (import.meta.env.PROD && import.meta.env.VITE_CLARITY_PROJECT_ID && !isAdminRoute) {
  (function(c: any, l: Document, a: string, r: string, i: string, t: any, y: any) {
    c[a] = c[a] || function() { (c[a].q = c[a].q || []).push(arguments) };
    t = l.createElement(r);
    t.async = 1;
    t.src = "https://www.clarity.ms/tag/" + i;
    y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(window, document, "clarity", "script", import.meta.env.VITE_CLARITY_PROJECT_ID, 0, 0);
}

// Initialize Google Analytics 4 (production only, excluding admin routes)
if (import.meta.env.PROD && import.meta.env.VITE_GA_MEASUREMENT_ID && !isAdminRoute) {
  // Load gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${import.meta.env.VITE_GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize dataLayer and gtag
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).gtag = function(...args: any[]) {
    (window as any).dataLayer.push(arguments);
  };
  (window as any).gtag('js', new Date());
  (window as any).gtag('config', import.meta.env.VITE_GA_MEASUREMENT_ID);
}

// Initialize PostHog product analytics (production only, excluding admin routes).
// Pageview manual: la navegación de participante es por estado (setView), no por
// URL → lo emite appAnalytics.trackAppView. Session replay desactivado: ya usamos
// Microsoft Clarity para grabaciones/heatmaps (evita duplicar y costo).
if (import.meta.env.PROD && import.meta.env.VITE_POSTHOG_KEY && !isAdminRoute) {
  // Init DIFERIDO (dynamic import tras el primer paint): posthog-js (~70KB gz)
  // sale del critical path. Con capture_pageview:false y pageviews manuales
  // (appAnalytics.trackAppView), inicializar unos ms más tarde no pierde eventos.
  const initPosthog = () =>
    import('posthog-js')
      .then(({ default: posthog }) => {
        posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
          api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
          person_profiles: 'identified_only',
          capture_pageview: false,
          disable_session_recording: true,
        });
      })
      .catch(() => {});
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(initPosthog, { timeout: 3000 });
  } else {
    setTimeout(initPosthog, 1500);
  }
}

// Fallos de import dinámico: Vite emite 'vite:preloadError' cuando falla el
// preload de un chunk (o el import mismo). Acá NO cancelamos el evento ni
// recargamos, a propósito:
//
//   - `event.preventDefault()` le decía a Vite que NO relanzara el error, y su
//     `baseModule().catch(handlePreloadError)` terminaba RESOLVIENDO `undefined`.
//     Ese módulo fantasma es el que producía "undefined is not an object
//     (evaluating 'e.Layout')" y toda la familia de errores por componente:
//     el `.then(m => ({ default: m.X }))` dereferenciaba un undefined.
//   - Recargar acá, además, se adelantaba a cualquier reintento y disparaba una
//     recarga completa incluso ante un bache de red del que Vite se recupera solo
//     (un preload de dependencia falla, pero `baseModule()` igual carga bien).
//
// Dejando propagar el error, el import rechaza de verdad y la recuperación queda
// en un solo lugar: lazyWithReload (reintenta, y recién ahí recarga). Los imports
// dinámicos fuera de lazyWithReload van dentro de flujos con su propio manejo de
// error, y el onError del ErrorBoundary raíz sigue siendo la red de seguridad.
window.addEventListener('vite:preloadError', (event) => {
  console.warn('[App] preload de chunk falló; lo maneja lazyWithReload', event);
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

// Fallback de carga para los chunks lazy de App (pantallas del participante que
// ahora se cargan on-demand). Spinner sutil a pantalla completa; se muestra solo
// mientras se resuelve un chunk recién solicitado (breve, y cacheado por el SW).
const RootFallback = (
  <div className="min-h-screen flex items-center justify-center bg-bg">
    <div className="animate-spin h-8 w-8 border-2 border-primary/30 border-t-primary rounded-full" />
  </div>
);

// v6.11.218: si el service worker recargó esta ventana porque tenía una versión
// vieja (`?actualizada=1`), se anota para el aviso y se saca de la URL antes de
// que la app la lea.
consumeUpdatedMarker();

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      // v6.11.67: pantalla real con botón de recarga (que además purga los
      // caches del SW) en vez del <div> pelado que dejaba a la persona sin
      // salida. Ver components/AppErrorFallback.tsx.
      fallback={({ error }) => <AppErrorFallback error={error} />}
      // Auto-recuperación de chunks obsoletos: si el error parece de chunk/vendor
      // viejo (tras un redeploy), recargamos para tomar los assets nuevos en vez
      // de dejar al usuario en la pantalla de error. Guard anti-loop compartido.
      onError={(error) => {
        if (isStaleChunkError(error)) reloadForStaleChunk('errorBoundary');
      }}
    >
      <QueryProvider>
        <AuthProvider>
          <React.Suspense fallback={RootFallback}>
            <App />
          </React.Suspense>
          <AppUpdateBanner />
          <LeagueDataUpdateBanner />
        </AuthProvider>
      </QueryProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

// Register Service Worker for PWA functionality — PRODUCCIÓN ÚNICAMENTE.
// En dev el SW cachea el bundle (cache-first) y al recargar sirve código viejo,
// rompiendo HMR (verías loaders/UI desactualizada). Por eso en dev NO se registra
// y además se desregistra cualquier SW previo + se limpian sus caches (ver else).
if (import.meta.env.PROD && !isRenderRoute && 'serviceWorker' in navigator) {
  // v6.11.41.00 — Le decimos al SW si esta ventana es la PWA instalada o una
  // pestaña del navegador. Sin esto, el click de una push podía enfocar una
  // pestaña vieja de Chrome en vez de la app (ver utils/swClientContext.ts).
  // Se registra antes del load: el SW puede preguntar apenas llega una push.
  initSwClientContext();

  // v6.11.42.00 — Cuando el lanzamiento lo hace el navegador (openWindow sobre
  // una ventana congelada, un shortcut del manifest), la URL de destino llega
  // por launchQueue en vez de por client.navigate(). Sin consumidor se descarta.
  initLaunchHandler();

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        console.log('[App] Service Worker registered with scope:', registration.scope);

        // Check for updates every hour
        setInterval(() => {
          registration.update().catch((error) => {
            // Silently fail if network is unavailable - SW will retry on next interval
            console.warn('[App] Service Worker update check failed (likely offline):', error.message);
          });
        }, 60 * 60 * 1000);

        // v6.11.18.04 — Chequeo de update ÁGIL al volver a la pestaña/app. El
        // chequeo horario dejaba una ventana de hasta 60' tras un deploy en la que
        // un usuario con la shell vieja en memoria, al navegar a una ruta lazy cuyo
        // chunk viejo ya no existe, pegaba "undefined is not an object (evaluating
        // 'e.<Componente>')" (chunk obsoleto → SPA fallback). Revalidando el SW al
        // recuperar visibilidad, el banner "nueva versión" aparece a los segundos
        // del deploy y el usuario actualiza ANTES de romper. Throttle de 2' para no
        // spamear updates en cada foco. No toca sw.js ni la recuperación reactiva.
        let lastUpdateCheck = 0;
        const eagerUpdateCheck = () => {
          if (document.visibilityState !== 'visible') return;
          const now = Date.now();
          if (now - lastUpdateCheck < 2 * 60 * 1000) return;
          lastUpdateCheck = now;
          registration.update().catch(() => { /* offline: reintenta en el próximo foco / hora */ });
        };
        document.addEventListener('visibilitychange', eagerUpdateCheck);

        // Listen for updates
        registration.onupdatefound = () => {
          const installingWorker = registration.installing;
          if (installingWorker) {
            installingWorker.onstatechange = () => {
              if (installingWorker.state === 'installed') {
                if (navigator.serviceWorker.controller) {
                  // New update available → avisar a la app para el banner no intrusivo
                  console.log('[App] New content available, refresh to update');
                  window.dispatchEvent(new CustomEvent('app-update-available'));
                } else {
                  // Content cached for offline use
                  console.log('[App] Content cached for offline use');
                }
              }
            };
          }
        };
      })
      .catch((error) => {
        console.error('[App] Service Worker registration failed:', error);
      });
  });
} else if (!import.meta.env.PROD && 'serviceWorker' in navigator) {
  // DEV: matar cualquier SW previo + sus caches para no servir bundles viejos
  // (causa de "veo loaders viejos / no aparecen mis cambios al recargar").
  navigator.serviceWorker
    .getRegistrations()
    .then((regs) => regs.forEach((r) => r.unregister()))
    .catch(() => {});
  if (typeof caches !== 'undefined') {
    caches.keys().then((keys) => keys.forEach((k) => caches.delete(k))).catch(() => {});
  }
}